import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero } from "@/components/site/PageHero";
import { ListingIntro } from "@/components/site/ListingIntro";
import { ListingEmpty } from "@/components/site/ListingEmpty";
import { ListingPagination } from "@/components/site/ListingPagination";
import { sortItems } from "@/lib/listing";
import { ArrowRight, Clock } from "lucide-react";

export default function Experiences() {
  const FB = { overline: "Tailored For Every Traveller", title: "Maharashtra Experiences", subtitle: "From honeymoons to student tours, pilgrimage to corporate offsites — experiences shaped around your client profiles.", image: "https://images.unsplash.com/photo-1695453461885-bcecb1c64320?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", seo_title: "Maharashtra Travel Experiences | Maha Sampada B2B", seo_description: "Family holidays, honeymoons, group tours, pilgrimage, wildlife, heritage, corporate offsites and more — Maharashtra experiences designed for travel partners." };
  const { hero, sections } = usePageCms("experiences", FB);
  const cfg = ((sections || []).find((s) => s.kind === "listing_config")) || {};
  const perPage = cfg.per_page || 24;
  const isList = (cfg.layout || "grid") === "list";
  useSeo({ title: hero.seo_title || FB.seo_title, description: hero.seo_description || FB.seo_description });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const resultsRef = useRef(null);
  useEffect(() => { api.get("/experiences").then((r) => setItems(r.data)); }, []);
  useEffect(() => { setPage(1); }, [cfg.sort, perPage]);
  const goPage = (p) => { setPage(p); resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const sorted = sortItems(items, cfg.sort || "default", "title");
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * perPage, safePage * perPage);

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle}
        image={hero.image} crumbs={[{ label: "Experiences" }]} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <ListingIntro cfg={cfg} />
        {sorted.length === 0 ? (
          <ListingEmpty cfg={cfg} defaultTitle="No experiences to show yet" defaultBody="Please check back soon for Maharashtra experiences." testid="experience-empty" />
        ) : (
          <div ref={resultsRef} style={{ scrollMarginTop: 100 }}>
            <div className={`grid gap-6 ${isList ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
              {pageItems.map((e) => (
                <Link key={e.slug} to={`/experiences/${e.slug}`} data-testid={`experience-card-${e.slug}`}
                      className={`img-zoom hover-lift group relative block overflow-hidden rounded-2xl ${isList ? "h-56" : "h-72"}`}>
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <div className="overline mb-1 flex items-center gap-1 text-gold"><Clock className="h-3 w-3" />{e.duration}</div>
                    <h3 className="font-display text-2xl font-semibold text-cream">{e.title}</h3>
                    <p className="mt-1 text-sm text-cream/80 line-clamp-2">{e.intro}</p>
                    <span className="mt-2 inline-flex items-center gap-1 font-accent text-xs font-semibold text-gold opacity-0 transition-opacity group-hover:opacity-100">Learn more <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
            <ListingPagination page={safePage} totalPages={totalPages} onChange={goPage} testid="experience-pagination" />
          </div>
        )}
      </section>
    </div>
  );
}
